import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Input } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import {
     blankRow,
     buildRow,
     isSkippedRow,
     rowFieldProblems,
     rowProblem,
     toCommitRows,
} from "../lcvParticipantRow";
import LcvImportHistoryGrid from "./LcvImportHistoryGrid";
import { lcvParticipantsConstants } from "../../../../../_redux/_constants/lcvParticipants.constants";
import {
     lcvParticipantsGetJobRolesRequest,
     lcvParticipantsImportRequest,
} from "../../../../../_redux/_actions/lcvParticipants.action";

// The file is read here and stays editable. The sheet is a starting point, not the final word:
// anything mistyped in it is corrected on screen rather than sent back to the supplier.
//
// Only rows at an Existing Pro+ dealer reach the grid. Anything else in the status column is
// ignored outright, because nothing on such a row is ever read. The count is still reported above
// the table so nothing disappears silently.

// Shown before a file is picked, so an administrator knows what pressing import will do before it
// happens rather than after. These must stay in step with the rules the backend applies.
const UPLOAD_RULES = [
     "Only rows marked Existing Pro+ are imported. Every other row is ignored.",
     "An email address we do not know gets a login, a participant record and an invitation.",
     "An email address we already know keeps its login. Its role is updated to match the file.",
     "Accreditation only ever moves from N to Y. A Y is never taken away, and a blank counts as N.",
     "A dealer we do not know is created and flagged as LCV. A dealer we know is flagged as LCV.",
     "An area is never changed. Whatever we already hold for a dealer or an area manager stays.",
     "An area manager we do not know is created and invited.",
     "Running the same file twice changes nothing, so a repeated import is safe.",
];

const ACCREDITED_VALUES = ["Y", "N"];
const GRID_MAX_HEIGHT = "58vh";

export default function LcvParticipantImport() {
     const actionDispatch = useDispatch();

     // The role list is a lookup, so it comes from the backend rather than a list held here that
     // would go stale the moment a role is added or renamed.
     const jobRoles = useSelector((state) => state?.lcvParticipants?.jobRoles);

     const isImporting = useSelector((state) =>
          (state?.loading?.pendingRequests || []).includes(
               lcvParticipantsConstants.LCV_PARTICIPANTS_IMPORT_REQUEST,
          ),
     );

     const [rows, setRows] = useState([]);
     const [fileName, setFileName] = useState("");
     const [skippedCount, setSkippedCount] = useState(0);
     const [readError, setReadError] = useState("");

     useEffect(() => {
          actionDispatch(lcvParticipantsGetJobRolesRequest({}));
     }, [actionDispatch]);

     const roleNames = useMemo(
          () => (jobRoles || []).map((role) => role.name),
          [jobRoles],
     );

     const readFile = (event) => {
          const file = event.target.files && event.target.files[0];

          if (!file) {
               return;
          }

          setFileName(file.name);
          setReadError("");

          const reader = new FileReader();

          reader.onload = (loadEvent) => {
               try {
                    const workbook = XLSX.read(loadEvent.target.result, {
                         type: "array",
                    });

                    const sheet = workbook.Sheets[workbook.SheetNames[0]];
                    const sheetRows = XLSX.utils.sheet_to_json(sheet, {
                         defval: "",
                    });

                    const parsed = sheetRows.map((sheetRow, index) => buildRow(sheetRow, index + 2));
                    const importable = parsed.filter((row) => !isSkippedRow(row));

                    setRows(importable);
                    setSkippedCount(parsed.length - importable.length);
               } catch (error) {
                    setRows([]);
                    setSkippedCount(0);
                    setReadError("That file could not be read as a spreadsheet.");
               }
          };

          reader.readAsArrayBuffer(file);
     };

     // Kept stable so the cell components below never have to be rebuilt.
     const setRowField = useCallback((rowIndex, field, value) => {
          setRows((previous) =>
               previous.map((row, index) =>
                    index === rowIndex ? { ...row, [field]: value } : row,
               ),
          );
     }, []);

     const removeRow = useCallback((rowIndex) => {
          setRows((previous) => previous.filter((row, index) => index !== rowIndex));
     }, []);

     // Somebody missing from the file is added here rather than by editing the file and uploading it
     // again. The new row is numbered past every row already held, so it cannot collide with a sheet
     // row number that another row is still reporting.
     const addRow = useCallback(() => {
          setRows((previous) => {
               const lastNumber = previous.reduce(
                    (highest, row) => Math.max(highest, row.sheetRowNumber || 0),
                    1,
               );

               return [...previous, blankRow(lastNumber + 1)];
          });
     }, []);

     const gridRows = useMemo(
          () =>
               rows.map((row, index) => ({
                    ...row,
                    rowIndex: index,
                    rowNumber: index + 1,
                    problems: rowFieldProblems(row),
                    problem: rowProblem(row),
               })),
          [rows],
     );

     const problemCount = gridRows.filter((row) => row.problem).length;
     const readyCount = gridRows.length - problemCount;

     const submit = () => {
          actionDispatch(
               lcvParticipantsImportRequest({
                    fileName,
                    participants: toCommitRows(rows),
               }),
          );
     };

     // Each cell component is built once. Rebuilding them on every render would make React treat
     // each keystroke as a new component, throwing away the input and the caret with it.
     const cells = useMemo(() => {
          // Only the cell that carries the problem turns red, so the eye lands on the thing to fix
          // rather than on the whole row.
          const cell = (field, control) => (cellProps) => {
               const problem = cellProps.dataItem.problems[field];

               return (
                    <Box
                         component="td"
                         title={problem || undefined}
                         sx={
                              problem
                                   ? {
                                          backgroundColor: "error.light",
                                          color: "error.contrastText",
                                     }
                                   : undefined
                         }
                    >
                         {control(cellProps)}
                    </Box>
               );
          };

          const textCell = (field) =>
               cell(field, (cellProps) => (
                    <Input
                         style={{ width: "100%" }}
                         value={cellProps.dataItem[field]}
                         onChange={(event) =>
                              setRowField(
                                   cellProps.dataItem.rowIndex,
                                   field,
                                   event.value,
                              )
                         }
                    />
               ));

          return {
               dealerCode: textCell("dealerCode"),
               area: textCell("area"),
               areaManager: textCell("areaManager"),
               areaManagerEmail: textCell("areaManagerEmail"),
               dealerName: textCell("dealerName"),
               firstName: textCell("firstName"),
               surname: textCell("surname"),
               email: textCell("email"),
               role: cell("role", (cellProps) => (
                    <DropDownList
                         style={{ width: "100%" }}
                         data={roleNames}
                         value={cellProps.dataItem.role || null}
                         defaultItem="Pick a role"
                         onChange={(event) =>
                              setRowField(
                                   cellProps.dataItem.rowIndex,
                                   "role",
                                   event.value,
                              )
                         }
                    />
               )),
               accredited: cell("accredited", (cellProps) => (
                    <DropDownList
                         style={{ width: "100%" }}
                         data={ACCREDITED_VALUES}
                         value={cellProps.dataItem.accredited || null}
                         onChange={(event) =>
                              setRowField(
                                   cellProps.dataItem.rowIndex,
                                   "accredited",
                                   event.value,
                              )
                         }
                    />
               )),
               remove: (cellProps) => (
                    <td>
                         <Tooltip title="Remove this row" arrow>
                              <IconButton
                                   aria-label="Remove this row"
                                   onClick={() =>
                                        removeRow(cellProps.dataItem.rowIndex)
                                   }
                              >
                                   <DeleteIcon />
                              </IconButton>
                         </Tooltip>
                    </td>
               ),
          };
     }, [removeRow, roleNames, setRowField]);

     return (
          <div>
               <Typography variant="body1" sx={{ mb: 2 }}>
                    Upload the LCV participant list, correct anything typed
                    badly in it, then import them all in one go.
               </Typography>

               {!fileName && (
                    <Box component="section" sx={{ mb: 3 }}>
                         <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                              What this upload does
                         </Typography>

                         <Box component="ul" sx={{ m: 0, pl: 3 }}>
                              {UPLOAD_RULES.map((rule) => (
                                   <Typography component="li" variant="body2" key={rule} sx={{ mb: 0.5 }}>
                                        {rule}
                                   </Typography>
                              ))}
                         </Box>
                    </Box>
               )}

               <Box sx={{ mb: 2 }}>
                    <input
                         type="file"
                         accept=".xlsx,.xls,.csv"
                         onChange={readFile}
                    />
               </Box>

               {fileName && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                         {fileName} - {gridRows.length} to import, {readyCount}{" "}
                         ready, {problemCount} to fix, {skippedCount} ignored,
                         not Existing Pro+
                    </Typography>
               )}

               {readError && (
                    <Typography variant="body2" sx={{ mb: 1, color: "error.main" }}>
                         {readError}
                    </Typography>
               )}

               {gridRows.length > 0 && (
                    <>
                         {/* The grid takes a height of its own so the table scrolls in both
                             directions with its header pinned, instead of the page growing until
                             the horizontal bar is somewhere off the bottom of the screen. */}
                         <Grid
                              data={gridRows}
                              className="sba-area-data-grid"
                              style={{ maxHeight: GRID_MAX_HEIGHT }}
                         >
                              <GridColumn field="rowNumber" title="Row" width="70px" />
                              <GridColumn field="dealerCode" title="Dealer Code" width="130px" cells={{ data: cells.dealerCode }} />
                              <GridColumn field="area" title="Area" width="100px" cells={{ data: cells.area }} />
                              <GridColumn field="dealerName" title="Dealer Name" width="230px" cells={{ data: cells.dealerName }} />
                              <GridColumn field="firstName" title="First Name" width="160px" cells={{ data: cells.firstName }} />
                              <GridColumn field="surname" title="Surname" width="160px" cells={{ data: cells.surname }} />
                              <GridColumn field="email" title="Email" width="260px" cells={{ data: cells.email }} />
                              <GridColumn field="role" title="Role" width="220px" cells={{ data: cells.role }} />
                              <GridColumn field="accredited" title="Accredited" width="130px" cells={{ data: cells.accredited }} />
                              <GridColumn field="areaManager" title="Area Manager" width="180px" cells={{ data: cells.areaManager }} />
                              <GridColumn field="areaManagerEmail" title="Area Manager Email" width="250px" cells={{ data: cells.areaManagerEmail }} />
                              <GridColumn title=" " width="80px" cells={{ data: cells.remove }} />
                         </Grid>

                         <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end" }}>
                              <Tooltip title="Add a row">
                                   <IconButton
                                        aria-label="Add a row"
                                        onClick={addRow}
                                        sx={{
                                             backgroundColor: "#1976d2",
                                             color: "#fff",
                                             "&:hover": { backgroundColor: "#1565c0" },
                                        }}
                                   >
                                        <AddIcon />
                                   </IconButton>
                              </Tooltip>
                         </Box>

                         <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
                              <Button
                                   type="button"
                                   themeColor="primary"
                                   disabled={problemCount > 0 || readyCount === 0 || isImporting}
                                   onClick={submit}
                              >
                                   {isImporting
                                        ? "Importing..."
                                        : `Import ${readyCount} participant${readyCount === 1 ? "" : "s"}`}
                              </Button>

                              {problemCount > 0 && (
                                   <Typography variant="body2">
                                        Fix the cells marked in red first, or
                                        remove those rows.
                                   </Typography>
                              )}
                         </Box>
                    </>
               )}

               {/* Only ever one table on screen. The history belongs to the idle state, so it gives
                   way to the rows being prepared and to the outcome afterwards. */}
               {!fileName && <LcvImportHistoryGrid />}
          </div>
     );
}
